/**
 * One forecast line, rendered column by column from the catalogue (f11.1).
 *
 * Cell rules:
 * - identity and calculated columns are plain text, always;
 * - a month column of a closed month is text, hatched (`.col-closed`),
 *   titled "<Mon yyyy> is closed — Actual" and `aria-readonly` (f11.5);
 * - with Override on, Sales rate and headcount are disabled and greyed, and
 *   the fixed-service months become the editable ones (BR-F11.1-10);
 * - a caller who may not edit this row, or a cycle not open for input, gets
 *   text everywhere and no form control at all;
 * - f11.6: the cell a validation warning names gets `.cell-warning` and the
 *   message as its title. It stays an enabled input: warnings never block.
 */

import type { ForecastGrid, ForecastLine, ForecastLineMonth, ForecastMoney } from '@/api/client';

import { CalculatedCell } from './cells/CalculatedCell';
import { EditableFlagCell } from './cells/EditableFlagCell';
import { EditableNumberCell } from './cells/EditableNumberCell';
import { EditableSelectCell } from './cells/EditableSelectCell';
import { IdentityCell } from './cells/IdentityCell';
import type { ForecastColumn, LineField } from './forecastColumns';
import {
  EMPTY,
  formatDecimal1,
  formatDecimal2,
  formatMoney,
  rateHint,
} from './forecastView';
import { closedCellTitle, isActualMonth } from './monthWindow';
import type { CellChange } from './useUpdateForecastLine';
import { ValidationWarningBadge } from './validation/ValidationWarningBadge';
import {
  visibleEngineWarnings,
  warningForField,
  warningForHeadcount,
} from './validation/validationWarningCatalog';

interface ForecastRowProps {
  line: ForecastLine;
  grid: ForecastGrid;
  columns: readonly ForecastColumn[];
  onSave: (line: ForecastLine, change: CellChange) => void;
}

type IdentityField = Extract<
  LineField,
  | 'element_code'
  | 'country_code'
  | 'program_name'
  | 'account_name'
  | 'opportunity_name'
  | 'delivery_manager_name'
  | 'opportunity_owner_name'
  | 'product'
  | 'location_name'
>;

const CLIPPED: ReadonlySet<string> = new Set(['program_name', 'account_name', 'opportunity_name']);

function money(amount: string, currency: string): ForecastMoney {
  return { amount, currency_code: currency };
}

function ReadOnlyValue({ text, closedTitle, warning }: { text: string; closedTitle?: string; warning?: string }) {
  return (
    <td
      className={`${closedTitle ? 'col-closed' : 'col-ro'} num${warning && !closedTitle ? ' cell-warning' : ''}`}
      title={closedTitle ?? warning}
      aria-readonly={closedTitle ? true : undefined}
    >
      {text}
    </td>
  );
}

/** f11.2: a line added by hand from the master (band 2), not by the Kimble dump. */
export const MANUAL_ORIGIN = 'MANUAL';
export const MANUAL_TOOLTIP = 'Added to this cycle by hand (not in the Kimble dump)';

function ElementCell({ line }: { line: ForecastLine }) {
  const validation = line.validation_warnings ?? [];
  const engine = visibleEngineWarnings(line.result?.warnings ?? [], validation);
  return (
    <IdentityCell text={line.element_code} mono sticky warning={warningForField(validation, 'element_code')}>
      {line.origin === MANUAL_ORIGIN && (
        <span className="badge badge-purple fg-manual" title={MANUAL_TOOLTIP}>
          Manual
        </span>
      )}
      <ValidationWarningBadge
        count={validation.length + engine.length}
        warnings={validation}
        engineWarnings={line.result?.warnings ?? []}
      />
      {line.stale && <span className="fg-pill">Recalculating…</span>}
      {line.absent_from_source && <span className="fg-absent">Not in latest dump</span>}
    </IdentityCell>
  );
}

export function ForecastRow({ line, grid, columns, onSave }: ForecastRowProps) {
  const writable = grid.editable && line.can_edit;
  const locked = line.overwrite_fixed_service;
  const currency = line.currency_code;
  const monthOf = (period: number): ForecastLineMonth | undefined =>
    line.months.find((m) => m.period_month === period);
  const header = (period: number) => grid.months.find((m) => m.period_month === period);
  const validation = line.validation_warnings ?? [];

  function monthCell(column: ForecastColumn) {
    const period = column.month ?? 0;
    const month = monthOf(period);
    const info = header(period);
    const closedTitle = info && isActualMonth(info) ? closedCellTitle(info, grid.cycle.period_year) : undefined;
    const label = `${column.header} ${(column.field.split('.')[1] ?? '').replace(/_/g, ' ')} for ${line.element_code}`;

    if (column.field === 'months.headcount') {
      const text = formatDecimal1(month?.headcount);
      const warning = warningForHeadcount(validation, period);
      if (!writable || closedTitle) {
        return <ReadOnlyValue key={column.id} text={text} closedTitle={closedTitle} warning={warning} />;
      }
      return (
        <EditableNumberCell
          key={column.id}
          value={month?.headcount ?? null}
          display={text}
          label={label}
          disabled={locked}
          warning={warning}
          onCommit={(value) => value !== null && onSave(line, { months: [{ period_month: period, headcount: value }] })}
        />
      );
    }

    if (column.field === 'months.fixed_service_amount') {
      const text = formatMoney(month?.fixed_service_amount);
      if (!writable || closedTitle || !locked) {
        return <ReadOnlyValue key={column.id} text={text} closedTitle={closedTitle} />;
      }
      return (
        <EditableNumberCell
          key={column.id}
          value={month?.fixed_service_amount.amount ?? null}
          display={text === EMPTY ? '0' : text}
          label={label}
          onCommit={(value) =>
            value !== null &&
            onSave(line, { months: [{ period_month: period, fixed_service_amount: money(value, currency) }] })
          }
        />
      );
    }

    const text = formatMoney(month?.revenue_adjustment);
    if (!writable || closedTitle) return <ReadOnlyValue key={column.id} text={text} closedTitle={closedTitle} />;
    return (
      <EditableNumberCell
        key={column.id}
        value={month?.revenue_adjustment.amount ?? null}
        display={text === EMPTY ? '0' : text}
        label={label}
        onCommit={(value) =>
          value !== null &&
          onSave(line, { months: [{ period_month: period, revenue_adjustment: money(value, currency) }] })
        }
      />
    );
  }

  function editableCell(column: ForecastColumn) {
    switch (column.field) {
      case 'status':
        return writable ? (
          <EditableSelectCell
            key={column.id}
            value={line.status?.code ?? null}
            options={grid.lookups.statuses}
            label={`Status for ${line.element_code}`}
            onCommit={(code) => onSave(line, { status: code })}
          />
        ) : (
          <td key={column.id} className="col-ro">{line.status?.name ?? EMPTY}</td>
        );
      case 'stage':
        return writable ? (
          <EditableSelectCell
            key={column.id}
            value={line.stage.code}
            options={grid.lookups.stages}
            fallback={line.stage.kimble_value}
            label={`Stage for ${line.element_code}`}
            onCommit={(code) => onSave(line, { stage: code })}
          />
        ) : (
          <td key={column.id} className="col-ro">{line.stage.name ?? line.stage.kimble_value ?? EMPTY}</td>
        );
      case 'sales_rate_override': {
        const hint = rateHint(line, grid);
        const text = formatMoney(line.sales_rate_override, { zeroAsEmpty: false });
        const warning = warningForField(validation, 'sales_rate_override');
        if (!writable) {
          return (
            <ReadOnlyValue
              key={column.id}
              text={line.sales_rate_override ? text : hint.placeholder || EMPTY}
              warning={warning}
            />
          );
        }
        return (
          <EditableNumberCell
            key={column.id}
            value={line.sales_rate_override?.amount ?? null}
            display={text}
            label={`Sales rate for ${line.element_code}`}
            placeholder={hint.placeholder}
            title={line.sales_rate_override ? 'Sales rate from the manager’s override' : hint.title}
            disabled={locked}
            warning={warning}
            allowEmpty
            onCommit={(value) =>
              onSave(line, { sales_rate_override: value === null ? null : money(value, currency) })
            }
          />
        );
      }
      case 'probability': {
        const text = formatDecimal2(line.probability);
        const warning = warningForField(validation, 'probability');
        if (!writable) return <ReadOnlyValue key={column.id} text={text} warning={warning} />;
        return (
          <EditableNumberCell
            key={column.id}
            value={line.probability ?? null}
            display={text}
            warning={warning}
            label={`Probability for ${line.element_code}`}
            onCommit={(value) => value !== null && onSave(line, { probability: value })}
          />
        );
      }
      case 'overwrite_fixed_service': {
        const warning = warningForField(validation, 'overwrite_fixed_service');
        return writable ? (
          <EditableFlagCell
            key={column.id}
            checked={locked}
            label={`Override fixed service for ${line.element_code}`}
            warning={warning}
            onCommit={(checked) => onSave(line, { overwrite_fixed_service: checked })}
          />
        ) : (
          <td key={column.id} className={`col-ro${warning ? ' cell-warning' : ''}`} title={warning}>
            {locked ? 'Override' : EMPTY}
          </td>
        );
      }
      default:
        return monthCell(column);
    }
  }

  function calculatedCell(column: ForecastColumn) {
    const result = line.result;
    const title = line.stale ? 'Recalculating…' : undefined;
    switch (column.field) {
      case 'result.fy_avg_headcount':
        return <CalculatedCell key={column.id} text={formatDecimal2(result?.fy_avg_headcount)} title={title} />;
      case 'result.fy_fixed_service':
        return <CalculatedCell key={column.id} text={formatMoney(result?.fy_fixed_service)} title={title} />;
      case 'result.fy_revenue_adjustment':
        return <CalculatedCell key={column.id} text={formatMoney(result?.fy_revenue_adjustment)} title={title} />;
      case 'result.fy_revenue':
        return <CalculatedCell key={column.id} text={formatMoney(result?.fy_revenue)} title={title} />;
      case 'result.unweighted_revenue':
        return <CalculatedCell key={column.id} text={formatMoney(result?.unweighted_revenue)} title={title} />;
      default:
        return <CalculatedCell key={column.id} text={EMPTY} />;
    }
  }

  return (
    <tr>
      {columns.map((column) => {
        if (column.kind === 'identity') {
          if (column.field === 'element_code') return <ElementCell key={column.id} line={line} />;
          const field = column.field as IdentityField;
          return (
            <IdentityCell
              key={column.id}
              text={line[field]}
              mono={column.format === 'mono'}
              clip={CLIPPED.has(field)}
            />
          );
        }
        if (column.kind === 'calculated') return calculatedCell(column);
        return editableCell(column);
      })}
    </tr>
  );
}
